import { useFeatureFlags } from '@audibene/ta.react-feature-flags'
import { useCallback, useMemo } from 'react'
import { useQuery } from 'react-query'
import type { Customer } from '../services/useCustomersService/types'
import { CustomerSearchQuery, useAppointmentsService, useOpportunityService } from '../services'
import { useCustomerSearch } from '../useCustomerSearch'
import { opportunityToLifecycle } from '../useOpportunityLifecycle/opportunityToLifecycle'
import { useDeliveryModel } from '../useDeliveryModel'
import { AppointmentStatus, defaultQueryOptions } from 'constants/'
import type { OpportunityLifecycle } from 'types'
import { LegacyCustomerOpportunityLifecycle } from 'types'
import { arrayToMapByPropName, findLatestOpportunities, groupBy, take } from 'utils'

const activeAppointmentStatuses = [AppointmentStatus.COMPLETED, AppointmentStatus.CONFIRMED, AppointmentStatus.PENDING]

interface UseHookContext {
  data?: LegacyCustomerOpportunityLifecycle[]
  hasMore: boolean
  isError: boolean
  isFetched: boolean
  isFetching: boolean
  isLoading: boolean
  maxResultLimit: number
}

const CUSTOMER_SEARCH_PAGE_SIZE = 300
const UI_MAX_RESULTS_DISPLAY = 25
const hookName = 'useCustomerOpportunitySearchLegacy_opportunities'

const useCustomerOpportunitySearchLegacy = (query: CustomerSearchQuery, isEnabled = true): UseHookContext => {
  const [isNeoEnabled] = useFeatureFlags(['NEO'])
  const { deliveryModel } = useDeliveryModel()
  const { getLatestOpportunities, getCurrentOpportunity } = useOpportunityService()
  const { getAppointments } = useAppointmentsService()

  const customersQuery = {
    ...query,
    page_size: CUSTOMER_SEARCH_PAGE_SIZE,
  }

  const {
    data: customersResponse,
    isError: haveAccountsErrored,
    isFetched: haveAccountsFetched,
    isFetching: areAccountsFetching,
    isLoading: areAccountsLoading,
  } = useCustomerSearch(customersQuery, { enabled: isEnabled })

  const customers = customersResponse?.data
  const customerGids = customers?.map((c) => c.salesforceId)?.filter(Boolean) ?? []
  const isOpportunityQueryEnabled = Boolean(!areAccountsLoading && customerGids.length > 0 && isEnabled)

  const fetchOpportunityLifecycles = useCallback(async (): Promise<OpportunityLifecycle[]> => {
    const { data: opportunities } = await getLatestOpportunities({
      customerSalesforceId: customerGids,
      pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
    })
    const { data: currentOpportunities } = await getCurrentOpportunity({
      customerSalesforceId: customerGids,
    })

    const byCustomer = groupBy(opportunities ?? [], (o) => o.customerSalesforceId ?? o.customerGid ?? '')
    const currentByCustomer = groupBy(currentOpportunities ?? [], (o) => o.customerSalesforceId ?? o.customerGid ?? '')

    const latestPerCustomer: Array<{
      gid: string
      latest: NonNullable<ReturnType<typeof findLatestOpportunities>[0]>
    }> = []

    for (const gid of customerGids) {
      const group = byCustomer[gid] ?? []
      const currentGroup = currentByCustomer[gid] ?? []
      const [latest] = findLatestOpportunities(group, currentGroup[0], isNeoEnabled)

      if (latest) {
        const opportunityGid = (latest as { gid?: string }).gid ?? latest.salesforceId ?? latest.id

        if (opportunityGid) {
          latestPerCustomer.push({ gid: opportunityGid, latest })
        } else {
          latestPerCustomer.push({ gid: '', latest })
        }
      }
    }

    const opportunityGids = latestPerCustomer.map(({ gid }) => gid).filter(Boolean)
    const opportunityGidsWithAppointment = new Set<string>()

    if (opportunityGids.length > 0) {
      const { data: appointments } = await getAppointments({
        opportunityGids,
        statuses: activeAppointmentStatuses,
      })

      for (const apt of appointments ?? []) {
        if (apt.opportunity_gid) opportunityGidsWithAppointment.add(apt.opportunity_gid)
      }
    }

    const lifecycles: OpportunityLifecycle[] = []

    for (const { gid: opportunityGid, latest } of latestPerCustomer) {
      const lifecycle = opportunityToLifecycle(latest, {
        hasAppointment: opportunityGid ? opportunityGidsWithAppointment.has(opportunityGid) : false,
      })

      if (lifecycle) lifecycles.push(lifecycle)
    }
    return lifecycles
  }, [customerGids, getAppointments, getCurrentOpportunity, getLatestOpportunities, isNeoEnabled])

  const {
    data: opportunityLifecycles = [],
    isError: haveOpportunitiesErrored,
    isFetched: haveOpportunitiesFetched,
    isFetching: areOpportunitiesFetching,
    isLoading: areOpportunitiesLoading,
  } = useQuery([hookName, customerGids, deliveryModel], fetchOpportunityLifecycles, {
    ...defaultQueryOptions,
    enabled: isOpportunityQueryEnabled,
  })

  const isError = haveAccountsErrored || haveOpportunitiesErrored
  const isFetched = haveAccountsFetched || haveOpportunitiesFetched
  const isFetching = areAccountsFetching || areOpportunitiesFetching
  const isLoading = areAccountsLoading || areOpportunitiesLoading
  const opportunityMapByCustomerGid = arrayToMapByPropName(opportunityLifecycles, 'customer_gid')

  const data = useMemo(() => {
    const filtered =
      customers
        ?.filter((customer) => opportunityMapByCustomerGid[customer.salesforceId])
        .map<LegacyCustomerOpportunityLifecycle>((customer: Customer) => {
          const opportunity = opportunityMapByCustomerGid[customer.salesforceId]
          const end_of_trial_period = opportunity?.end_of_trial_period
          const opportunity_gid = opportunity?.opportunity_gid

          return {
            ...customer,
            end_of_trial_period,
            opportunity_gid,
            status: opportunityMapByCustomerGid[customer.salesforceId]?.status,
          }
        }) ?? ([] as LegacyCustomerOpportunityLifecycle[])

    const results = take(filtered, UI_MAX_RESULTS_DISPLAY)
    const hasMore = filtered.length > UI_MAX_RESULTS_DISPLAY

    return { hasMore, results }
  }, [customers, opportunityMapByCustomerGid])

  return {
    data: data.results,
    hasMore: data.hasMore,
    isError,
    isFetched,
    isFetching,
    isLoading,
    maxResultLimit: UI_MAX_RESULTS_DISPLAY,
  }
}

export { useCustomerOpportunitySearchLegacy }
